/**
 * Generic List component using the render-props pattern.
 *
 * ADR-022 Pattern 2: Render Props for configurable rendering.
 *
 * Usage:
 *   <List items={resumes} keyExtractor={(r) => r.id} emptyMessage="No resumes yet.">
 *     {(resume) => <ResumeCard resume={resume} />}
 *   </List>
 */
import { Fragment, type ReactNode } from 'react';

export interface ListProps<T> {
  items: T[];
  children: (item: T, index: number) => ReactNode;
  keyExtractor?: (item: T) => string;
  emptyMessage?: string;
  className?: string;
}

export function List<T>({
  items,
  children,
  keyExtractor = (item: T) => String((item as { id: string }).id),
  emptyMessage = 'No items',
  className,
}: ListProps<T>) {
  if (items.length === 0) {
    return <div className="list-empty">{emptyMessage}</div>;
  }

  return (
    <ul className={`list ${className ?? ''}`}>
      {items.map((item, index) => (
        <Fragment key={keyExtractor(item)}>{children(item, index)}</Fragment>
      ))}
    </ul>
  );
}
